import { useState } from 'react';
import { Button, Field, Input, KeyValue, Section } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { CompanyDetail } from '../lib/types';
import { BizimhesapBalanceSummary } from './BizimhesapStatement';

/**
 * Bayi kartında BizimHesap carisi (F7-25, ADR-113): bayinin faturası ve tahsilatı bu cari koduna yazılır. Kod
 * BizimHesap cari listesinde doğrulanarak bağlanır; bağlantısız bayinin faturası gönderilmez.
 */
export function DealerBizimhesapSection({ company }: { company: CompanyDetail }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can, dealerScoped } = usePermissions();
  const manage = can('org.integration.manage') && !dealerScoped;
  const [code, setCode] = useState(company.bizimhesapCode ?? '');
  const save = useApiMutation(
    (next: string | null) => api.put(`/v1/admin/companies/${company.id}/bizimhesap`, { code: next }),
    {
      invalidate: [['company', company.id], ['companies'], ['bizimhesap']],
      success: t('common.saved'),
      silentFieldErrors: true,
    },
  );
  const error = fieldError(save.error, 'links.0.code') ?? fieldError(save.error, 'code');
  return (
    <Section
      title={t('admin.bizimhesap.dealerTitle')}
      description={t('admin.bizimhesap.dealerLead')}
      action={<HelpTip k="dealers.bizimhesap_code" />}
    >
      <KeyValue
        items={[
          {
            label: t('admin.bizimhesap.customerCode'),
            value: company.bizimhesapCode ?? t('admin.bizimhesap.notLinked'),
          },
          {
            label: t('admin.bizimhesap.linkedAt'),
            value: company.bizimhesapLinkedAt ? formatDate(company.bizimhesapLinkedAt, true) : '—',
          },
        ]}
      />
      {manage && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <Field
            label={t('admin.bizimhesap.customerCode')}
            help={<HelpTip k="dealers.bizimhesap_link" />}
            error={error}
          >
            {(p) => (
              <Input
                {...p}
                className="w-48"
                maxLength={64}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            )}
          </Field>
          <Button
            loading={save.isPending}
            disabled={!code.trim() || code.trim() === company.bizimhesapCode}
            onClick={() => save.mutate(code.trim())}
          >
            {t('admin.bizimhesap.link')}
          </Button>
          {company.bizimhesapCode && (
            <Button variant="ghost" loading={save.isPending} onClick={() => save.mutate(null)}>
              {t('admin.bizimhesap.unlink')}
            </Button>
          )}
        </div>
      )}
      <BizimhesapBalanceSummary key={company.bizimhesapCode ?? ''} companyId={company.id} />
      {save.error && !error && (
        <p role="alert" className="mt-2 text-sm text-rose">
          {save.error.title}
        </p>
      )}
    </Section>
  );
}
